import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentUiState,
} from '../../../../redux/selectors/dataModeler';
import { useCallback, useMemo } from 'react';
import { emptyCustomColumnDraft, listToHashMapFromKey } from '../utils';
import {
  dataModelerEditDraftDerivedColumnInput,
  dataModelerEditExistingDerivedColumnInput,
  dataModelerSetDerivedColumnEdit,
  DerivedDefinitionType,
  IDerivedColumnDraft,
} from '../../../../redux/slices/dataModeler';
import {
  ICdwTableColumnData,
  IEditDerivedColumnModel,
} from '../DataModelerCdwTableGrid/types';
import { IDataModelFieldTypeInput } from '../../../../utils/apollo/graphqlSchema';
import {
  typeParamsInputToTypeParamsFieldsFragment,
  typeParamsToTypeParamsInput,
} from '../../../../redux/slices/dataModeler/utils';
import { batch } from 'react-redux';
import { assertNotNil } from '../../../../utils/types';

const useEditDerivedColumnModel = ({
  viewKey,
  tableKey,
}: {
  viewKey: string;
  tableKey: string;
}): IEditDerivedColumnModel => {
  const dispatch = useAppDispatch();
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const hasActiveNewDraft = !!useAppSelector(dataModelerSelectCurrentUiState)
    ?.newDerivedColumns[tableKey];
  const editColumnInputsByColumn = useMemo(() => {
    const lst =
      draftEdits?.editColumnInputs?.filter((editInput) => {
        return editInput.viewKey === viewKey && editInput.tableKey === tableKey;
      }) ?? [];
    return listToHashMapFromKey(lst, 'column');
  }, [draftEdits?.editColumnInputs, viewKey, tableKey]);

  const editedDerivedColumn =
    useAppSelector(dataModelerSelectCurrentUiState)?.editDerivedColumns[
      tableKey
    ] ?? null;
  const customColumnEdit = editedDerivedColumn ?? emptyCustomColumnDraft;

  const updateEditedDerivedColumn = useCallback(
    (props: Partial<IDerivedColumnDraft> | null) => {
      if (props) {
        const draft = {
          ...customColumnEdit,
          ...props,
        };
        dispatch(dataModelerSetDerivedColumnEdit({ tableKey, draft }));
      } else {
        dispatch(dataModelerSetDerivedColumnEdit({ tableKey, draft: null }));
      }
    },
    [customColumnEdit, dispatch, tableKey],
  );

  const editedColumnName = editedDerivedColumn?.columnName ?? null;

  const canEditDerivedColumn = useCallback(
    (columnData: ICdwTableColumnData) => {
      return (
        !hasActiveNewDraft &&
        !editedColumnName &&
        (!!columnData.addFieldInput?.typeInput.typeParams?.customDefinition ||
          !!columnData.extendFieldInput?.typeInput.typeParams
            ?.customDefinition ||
          !!columnData.tableColumn.typeParams.customDefinition)
      );
    },
    [editedColumnName, hasActiveNewDraft],
  );

  const setEditedDerivedColumn = useCallback(
    (columnData: ICdwTableColumnData) => {
      if (!!editedColumnName) {
        return;
      }
      const typeInput: IDataModelFieldTypeInput | null =
        columnData.typeInput ??
        columnData.addFieldInput?.typeInput ??
        columnData.extendFieldInput?.typeInput ??
        (columnData.fieldMeta
          ? {
              fieldClass: columnData.fieldMeta.fieldClass,
              type: columnData.fieldMeta.type,
              typeParams: typeParamsToTypeParamsInput(
                columnData.tableColumn.typeParams,
              ),
            }
          : null);
      if (!typeInput) {
        // column is not mapped at all
        return;
      }
      const fieldType = typeInput.type;
      const typeParams = typeParamsInputToTypeParamsFieldsFragment(
        columnData.column,
        typeInput.typeParams,
      );
      const existingAlreadyEdited = editColumnInputsByColumn[columnData.column];
      const definition =
        existingAlreadyEdited?.customDefinition ??
        typeInput.typeParams?.customDefinition ??
        null;
      const definitionType: DerivedDefinitionType | null = !definition
        ? null
        : !!definition.rawSql
          ? 'RawSql'
          : !!definition.mappedString
            ? 'MappedString'
            : null;
      const editDraft: IDerivedColumnDraft = {
        columnName: columnData.column,
        fieldType,
        typeParams,
        originalTypeParams: columnData.originalTableColumn.typeParams,
        definitionType,
        definition,
        isPublished: !columnData.isNew,
      };
      updateEditedDerivedColumn(editDraft);
    },
    [editedColumnName, updateEditedDerivedColumn, editColumnInputsByColumn],
  );

  const cancelEditedDerivedColumn = useCallback(() => {
    updateEditedDerivedColumn(null);
  }, [updateEditedDerivedColumn]);

  const confirmEditedDerivedColumn = useCallback(() => {
    if (!editedDerivedColumn?.definition) {
      return;
    }
    batch(() => {
      assertNotNil(editedDerivedColumn.definition);
      if (editedDerivedColumn.isPublished) {
        dispatch(
          dataModelerEditExistingDerivedColumnInput({
            originalTypeParams: editedDerivedColumn.originalTypeParams,
            input: {
              column: editedDerivedColumn.columnName,
              viewKey,
              tableKey,
              customDefinition: editedDerivedColumn.definition,
            },
          }),
        );
      } else {
        dispatch(
          dataModelerEditDraftDerivedColumnInput({
            input: {
              column: editedDerivedColumn.columnName,
              viewKey,
              tableKey,
              customDefinition: editedDerivedColumn.definition,
            },
          }),
        );
      }
      updateEditedDerivedColumn(null);
    });
  }, [
    editedDerivedColumn,
    updateEditedDerivedColumn,
    dispatch,
    viewKey,
    tableKey,
  ]);

  return useMemo(() => {
    return {
      editedDerivedColumn,
      updateEditedDerivedColumn,
      canEditDerivedColumn,
      setEditedDerivedColumn,
      cancelEditedDerivedColumn,
      confirmEditedDerivedColumn,
    };
  }, [
    updateEditedDerivedColumn,
    canEditDerivedColumn,
    cancelEditedDerivedColumn,
    confirmEditedDerivedColumn,
    editedDerivedColumn,
    setEditedDerivedColumn,
  ]);
};

export default useEditDerivedColumnModel;
